import { motion } from "framer-motion";
import { Layers, TrendingDown, Wallet, Calendar, Shield, ArrowRight, ArrowDown } from "lucide-react";

const benefits = [
  {
    icon: TrendingDown,
    title: "Slash daily payments",
    desc: "Reduce your daily/weekly debits by up to 50%. Free up cash flow immediately.",
  },
  {
    icon: Layers,
    title: "One clean payment",
    desc: "Roll 2, 3, even 5+ MCAs into a single, predictable payment on one schedule.",
  },
  {
    icon: Calendar,
    title: "Longer terms",
    desc: "Stretch the payoff over 12–36 months instead of drowning in short-term factor rates.",
  },
  {
    icon: Shield,
    title: "Avoid default",
    desc: "Prevent stacking spiral, UCC filings, and lender defaults before it's too late.",
  },
];

const steps = [
  { n: 1, title: "Full stack review", desc: "Send us your current MCA contracts. We calculate your true blended cost." },
  { n: 2, title: "Consolidation offer", desc: "We match you with a lender who will pay off your MCAs and issue one term loan." },
  { n: 3, title: "MCAs paid off", desc: "Your existing MCAs are paid directly. Daily debits stop." },
  { n: 4, title: "One payment forward", desc: "You make a single, lower monthly (or weekly) payment on new terms." },
];

export default function McaConsolidation({ onApply }) {
  return (
    <section
      id="mca-consolidation"
      data-testid="mca-consolidation-section"
      className="relative py-24 md:py-32 bg-white overflow-hidden"
    >
      <div className="absolute top-40 -right-40 w-[500px] h-[500px] rounded-full bg-emerald-200/30 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-purple-200/30 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="grid lg:grid-cols-2 gap-10 items-end mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.25em] text-emerald-700">
              <Layers className="w-3.5 h-3.5" /> Debt Restructure
            </span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black mt-5 tracking-tight text-slate-900 leading-[1.05]">
              Stacked in MCAs?{" "}
              <span className="bg-gradient-to-r from-emerald-600 via-purple-600 to-fuchsia-600 bg-clip-text text-transparent">
                Let's fix that.
              </span>
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-lg text-slate-500 leading-relaxed"
          >
            Merchant cash advances stack up fast — daily debits, short terms,
            and factor rates that quietly bleed your cash flow. Our MCA
            Consolidation program pays off your existing advances and rolls
            them into <span className="font-semibold text-slate-900">one lower payment</span>{" "}
            on a longer term — so you can breathe again and get back to growing.
          </motion.p>
        </div>

        {/* Before / After */}
        <div className="grid md:grid-cols-[1fr_auto_1fr] gap-4 md:gap-6 items-center mb-16">
          {/* Before */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="rounded-3xl border-2 border-red-100 bg-red-50/40 p-6 md:p-7"
          >
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-red-600 mb-4">
              Before · Stacked MCAs
            </p>
            <div className="space-y-2.5">
              {[
                { name: "MCA #1 (Blue Bay)", debit: "$487/day", tag: "60 days left" },
                { name: "MCA #2 (Rapid Cap)", debit: "$312/day", tag: "90 days left" },
                { name: "MCA #3 (Cardinal)", debit: "$198/day", tag: "120 days left" },
              ].map((m) => (
                <div
                  key={m.name}
                  className="flex items-center justify-between bg-white rounded-xl border border-red-100 px-4 py-3"
                >
                  <div>
                    <p className="text-sm font-bold text-slate-900">{m.name}</p>
                    <p className="text-[11px] uppercase tracking-widest text-slate-400 mt-0.5 font-semibold">
                      {m.tag}
                    </p>
                  </div>
                  <p className="text-sm font-black text-red-600">{m.debit}</p>
                </div>
              ))}
              <div className="mt-4 pt-3 border-t border-red-200 flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Total daily debit
                </p>
                <p className="font-display text-2xl font-black text-red-600">$997/day</p>
              </div>
            </div>
          </motion.div>

          {/* Arrow */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="hidden md:flex items-center justify-center"
          >
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-600 to-fuchsia-600 grid place-items-center shadow-xl shadow-purple-500/40 rotate-[-90deg] md:rotate-0">
              <ArrowRight className="w-6 h-6 text-white" />
            </div>
          </motion.div>
          <div className="md:hidden flex justify-center py-2">
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-purple-600 to-fuchsia-600 grid place-items-center shadow-lg shadow-purple-500/40">
              <ArrowDown className="w-5 h-5 text-white" />
            </div>
          </div>

          {/* After */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="rounded-3xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-emerald-50/40 p-6 md:p-7 shadow-lg shadow-emerald-200/40"
          >
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700 mb-4">
              After · Consolidated
            </p>
            <div className="bg-white rounded-2xl border border-emerald-100 p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 grid place-items-center shadow-md shadow-emerald-500/30">
                  <Wallet className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-900">Rocket Cap Consolidation</p>
                  <p className="text-[11px] uppercase tracking-widest text-slate-400 mt-0.5 font-semibold">
                    24-month term
                  </p>
                </div>
              </div>
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">
                    Single daily payment
                  </p>
                  <p className="font-display text-3xl font-black text-emerald-600 mt-1">$412/day</p>
                </div>
                <span className="rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold px-3 py-1">
                  −58% cash out
                </span>
              </div>
            </div>
            <p className="mt-4 text-xs text-slate-500">
              Illustrative example. Actual terms depend on stack, revenue, and lender review.
            </p>
          </motion.div>
        </div>

        {/* Benefits */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {benefits.map((b, idx) => (
            <motion.div
              key={b.title}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: idx * 0.06 }}
              data-testid={`mca-consol-benefit-${idx}`}
              className="rounded-3xl bg-white border border-slate-100 p-6 hover:border-emerald-200 hover:shadow-xl hover:-translate-y-1 transition-all"
            >
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 grid place-items-center shadow-md shadow-emerald-500/30 mb-4">
                <b.icon className="w-5 h-5 text-white" />
              </div>
              <h3 className="font-display text-lg font-bold text-slate-900">{b.title}</h3>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">{b.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Process */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="rounded-3xl bg-gradient-to-br from-[#0b1121] to-[#1e1b4b] text-white p-8 md:p-10 mb-10 relative overflow-hidden"
        >
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-purple-500/25 blur-3xl" />
          <div className="relative">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-emerald-300">
              How consolidation works
            </p>
            <h3 className="font-display text-2xl md:text-3xl font-black mt-2">
              From stacked to stable in 4 steps.
            </h3>
            <div className="mt-8 grid md:grid-cols-4 gap-6">
              {steps.map((s) => (
                <div key={s.n} className="relative">
                  <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur border border-white/20 grid place-items-center font-bold text-emerald-300">
                    {s.n}
                  </div>
                  <p className="font-display font-bold mt-3">{s.title}</p>
                  <p className="text-sm text-white/60 mt-1 leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <div className="flex flex-wrap items-center justify-center gap-4">
          <button
            data-testid="mca-consol-cta"
            onClick={() =>
              onApply?.({
                funding_purpose: "MCA Consolidation",
                funding_amount: 100000,
              })
            }
            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white font-bold px-8 py-4 text-sm tracking-[0.15em] uppercase shadow-xl shadow-emerald-500/30 hover:-translate-y-0.5 transition-all"
          >
            Get consolidated
            <ArrowRight className="w-4 h-4" />
          </button>
          <a
            href="#contact"
            data-testid="mca-consol-talk"
            className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 text-slate-900 font-bold px-7 py-4 text-sm tracking-[0.15em] uppercase hover:border-emerald-400 hover:-translate-y-0.5 transition-all"
          >
            Talk to a specialist
          </a>
        </div>
        <p className="mt-5 text-center text-[11px] text-slate-400 max-w-2xl mx-auto">
          No credit pull · Free stack analysis · Payoff quotes handled directly with your current lenders
        </p>
      </div>
    </section>
  );
}
